import React, {useEffect, useMemo, useState} from 'react';

import {Button, createListCollection, Input, NumberInput, Portal, Select, Spinner, Text, Textarea, VStack} from '@chakra-ui/react';

import {useForm} from '@/uses/useForm';
import {useApi} from '@/uses/useApi';
import {Field, FileInput, LanguageField} from '@/components';
import {useLanguage} from '@/contexts/languageContext';

export const CreateOrEditGuide = (props) => {
    const guideId = props?.id;

    const [state, setState] = useState(-1);
    const {getLangObject, language, t} = useLanguage();
    const [guide, setGuide] = useState(null);
    const [defaultLanguage, setDefaultLanguage] = useState('fr');
    const api = useApi();

    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            title: getLangObject(),
            description: getLangObject(),
            file: null,
            typeId: null,
            cover: getLangObject(),
            sortOrder: 0,
        },
        {
            title: {
                custom: {
                    function: (value) => {
                        return Object.values(value).some((v) => v && v.length >= 3);
                    },
                    message: t('errors.pleaseFillFieldAllLanguages'),
                },
            },
            typeId: {
                required: {
                    value: true,
                    message: t('errors.pleaseFillField'),
                },
            },
            file: {
                required: {
                    value: true,
                    message: t('errors.pleaseFillField'),
                },
            },
        }
    );

    const [guideTypes, setGuideTypes] = useState(null);

    useEffect(() => {
        setState(() => 0);
        api.ApiService.get('guide_type', {getAll: true})
            .then((res) => {
                if (res?.data?.type === 'success') {
                    const types = res.data.value ?? [];
                    setGuideTypes(() => types);
                    /** Un seul type possible : inutile de le faire choisir */
                    if (!guideId && types.length === 1) {
                        handleInputChange('typeId', types[0].id);
                    }
                }
            })
            .catch(() => setGuideTypes(() => []));
        if (guideId) {
            api.ApiService.getOne('guide', guideId, {
                attributes: ['id', 'title', 'description', 'file', 'cover', 'sortOrder', 'typeId'],
            })
                .then((res) => {
                    if (res?.data?.type === 'success' && res?.data?.value) {
                        setGuide(() => res.data.value);
                    }
                })
        }
    }, []);

    useEffect(() => {
        if (guide !== null) {
            handleInputChanges({
                ...guide,
                description: guide.description ?? getLangObject(),
                cover: {...getLangObject(), ...(guide.cover ?? {})},
            });
            setDefaultLanguage(guide.title?.fr?.length > 0 ? 'fr' : 'en');
        }
        if ((!guideId || guide !== null) && guideTypes !== null) {
            setState(() => 1);
        }
    }, [guide, guideId, guideTypes]);

    const guideTypesCollection = useMemo(() => createListCollection({
        items: (guideTypes ?? []).map((type) => ({
            label: type?.[`${language}Title`] ?? type?.frTitle ?? '',
            value: String(type.id),
        })),
    }), [guideTypes, language]);

    return (
        <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
            {
                state === 1 ?
                    <VStack overflow="auto" flex={1} gap="5" p="5" w="100%" align="stretch" justify="flex-start">
                        <Field
                            label={t('wrappers.mandatoryLabel', {value: t('guides.guideType')})}
                            errors={formFieldErrors.typeId}
                        >
                            <Select.Root
                                collection={guideTypesCollection}
                                value={formData.typeId ? [String(formData.typeId)] : []}
                                onValueChange={(e) => {
                                    const value = e.value?.[0];
                                    handleInputChange('typeId', value ? parseInt(value, 10) : null);
                                }}
                            >
                                <Select.HiddenSelect/>
                                <Select.Control>
                                    <Select.Trigger>
                                        <Select.ValueText
                                            placeholder={t('sentences.selectEntity', {entity: t('guides.guideType')})}
                                        />
                                    </Select.Trigger>
                                    <Select.IndicatorGroup>
                                        <Select.Indicator/>
                                    </Select.IndicatorGroup>
                                </Select.Control>
                                <Portal>
                                    <Select.Positioner>
                                        <Select.Content>
                                            {guideTypesCollection.items.map((type) => (
                                                <Select.Item item={type} key={type.value}>
                                                    {type.label}
                                                    <Select.ItemIndicator/>
                                                </Select.Item>
                                            ))}
                                        </Select.Content>
                                    </Select.Positioner>
                                </Portal>
                            </Select.Root>
                        </Field>
                        <LanguageField
                            id="title"
                            errors={formFieldErrors.title}
                            label={t('wrappers.mandatoryLabel', {value: t('fields.title')})}
                            defaultLanguage={defaultLanguage}
                            renderElement={(lang) => <Input
                                value={formData.title[lang]}
                                onChange={(e) => {
                                    handleInputChange('title', {...formData.title, [lang]: e?.target?.value ?? ''});
                                }}
                                placeholder={t('guides.guideTitle')}
                            />}
                        />
                        <LanguageField
                            id="description"
                            errors={formFieldErrors.description}
                            label={t('wrappers.nonMandatoryLabel', {value: t('fields.description')})}
                            defaultLanguage={defaultLanguage}
                            renderElement={(lang) => <Textarea
                                value={formData.description?.[lang] ?? ''}
                                onChange={(e) => {
                                    handleInputChange('description', {
                                        ...formData.description,
                                        [lang]: e?.target?.value ?? ''
                                    });
                                }}
                                placeholder={t('guides.guideDescription')}
                                rows={4}
                            />}
                        />
                        <LanguageField
                            id="cover"
                            errors={formFieldErrors.cover}
                            label={t('wrappers.nonMandatoryLabel', {value: t('guides.guideCover')})}
                            defaultLanguage={defaultLanguage}
                            renderElement={(lang) => <VStack align="stretch" gap={1}>
                                <FileInput
                                    hasPreview
                                    accept="image/png, image/jpeg, image/webp"
                                    file={formData.cover?.[lang] ?? null}
                                    /** FileInput mémorise son premier onChange : on passe par la valeur précédente
                                     *  pour ne pas écraser la couverture de l'autre langue */
                                    onChange={(file) => handleInputChange('cover', (prevData) => ({
                                        ...prevData.cover,
                                        [lang]: (typeof file === 'function' ? file() : file) ?? null,
                                    }))}
                                    deleteText={t('sentences.deleteFile')}
                                    w="100%"
                                    maxW="36rem"
                                    aspectRatio={3 / 2}
                                />
                                <Text fontSize="xs" color="gray.500">{t('guides.guideCoverHint')}</Text>
                            </VStack>}
                        />
                        <Field
                            label={t('wrappers.nonMandatoryLabel', {value: t('fields.order')})}
                            errors={formFieldErrors.sortOrder}
                            display="flex"
                            alignItems="center"
                        >
                            <NumberInput.Root
                                value={formData.sortOrder}
                                precision={0}
                                step={1}
                                onValueChange={(e) => handleInputChange('sortOrder', e.valueAsNumber)}
                            >
                                <NumberInput.Input />
                                <NumberInput.Control>
                                    <NumberInput.IncrementTrigger />
                                    <NumberInput.DecrementTrigger />
                                </NumberInput.Control>
                            </NumberInput.Root>
                        </Field>
                        <Field
                            label={t('wrappers.mandatoryLabel', {value: t('guides.guidePdf')})}
                            errors={formFieldErrors.file}
                        >
                            <FileInput
                                accept="application/pdf"
                                file={formData.file}
                                deleteText={t('sentences.deleteFile')}
                                onChange={(file) => handleInputChange('file', typeof file === 'function' ? file() : file)}
                                bg="gray.100"
                            />
                        </Field>
                    </VStack>
                    :
                    <VStack flex={1} w="100%" align="center" justify="flex-start">
                        <Spinner size="lg" />
                    </VStack>
            }
            <Button
                variant="primary"
                mt="auto"
                height="auto"
                borderRadius=".5rem"
                style={{padding: '1rem 1.5rem'}}
                zIndex={10}
                position="relative"
                onClick={() =>
                    handleFormSubmit(async (data) => {
                        /** Le modèle refuse un objet multilingue entièrement vide : on envoie null */
                        const description = Object.values(data.description ?? {}).some((v) => v && v.length > 0)
                            ? data.description
                            : null;

                        const cover = Object.values(data.cover ?? {}).some(Boolean) ? data.cover : null;

                        if (guideId) {
                            return api.ApiService.update('guide', guideId, {...data, description, cover});
                        }
                        return api.ApiService.create('guide', {...data, description, cover});
                    }, props?.successCallback)
                }
            >
                {t('actions.validate')}
            </Button>
        </VStack>
    );
};

export default CreateOrEditGuide;
